import { useState, useMemo, useEffect } from "react";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Tooltip,
} from "@mui/material";
import CompareArrowsIcon from "@mui/icons-material/CompareArrows";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import RestartAltIcon from "@mui/icons-material/RestartAlt";

import FunctionsAfterValidation from "./FunctionsAfterValidation";

import { MATERIAL_TYPES, SelectionPanel } from "./components/SelectionPanel";
import { ComparisonTable } from "./components/ComparisonTable";
import { SummaryBar } from "./components/SummaryBar";
import {
  compareMaterials,
  getValidateLabel,
  getMaterialsBySelection,
} from "./utils/dataHelpers";

const EMPTY_SELECTION = {
  project: null,
  revision: null,
  drawing: null,
  sheet: null,
};

export default function ReconcileTab({ plant }) {
  const [leftSel, setLeftSel] = useState({
    ...EMPTY_SELECTION,
    type: MATERIAL_TYPES[0],
  });
  const [rightSel, setRightSel] = useState({
    ...EMPTY_SELECTION,
    type: MATERIAL_TYPES[1],
  });
  const [running, setRunning] = useState(false);
  const [compRows, setCompRows] = useState(null);
  const [filter, setFilter] = useState("all");
  const [selectedRows, setSelectedRows] = useState([]);

  // Get materials based on current selections using the new hierarchical system
  const leftMaterials = useMemo(
    () => getMaterialsBySelection(plant, leftSel),
    [plant, leftSel],
  );

  const rightMaterials = useMemo(
    () => getMaterialsBySelection(plant, rightSel),
    [plant, rightSel],
  );

  const handleValidate = async () => {
    setRunning(true);
    try {
      setCompRows(compareMaterials(leftMaterials, rightMaterials));

      setFilter("all");
    } finally {
      setRunning(false);
    }
  };
  const handleReset = () => {
    setLeftSel({
      ...EMPTY_SELECTION,
      type: MATERIAL_TYPES[0],
    });

    setRightSel({
      ...EMPTY_SELECTION,
      type: MATERIAL_TYPES[1],
    });

    setCompRows(null);
    setFilter("all");
    setRunning(false);
  };

  const matchRate = useMemo(() => {
    if (!compRows?.length) return null;
    return Math.round(
      (compRows.filter((r) => r.status === "match").length / compRows.length) *
        100,
    );
  }, [compRows]);

  const leftLabel = getValidateLabel(leftSel);
  const rightLabel = getValidateLabel(rightSel);

  // console.log("compRows", compRows);

  useEffect(() => {
    // Get the keys (IDs) where the value is true
    const selectedIds = Object.keys(selectedRows).filter(
      (key) => selectedRows[key],
    );
    console.log("Selected Rows Ids:", selectedIds);

    // Filter the original data array to find matching objects by key attribute
    const selectedRowsData = compRows?.filter((row) =>
      selectedIds.includes(row.key),
    );

    console.log("Selected Rows Full Data:", selectedRowsData);
  }, [selectedRows, compRows]); // Triggers every time a checkbox is clicked

  useEffect(() => {
    setSelectedRows([]);
  }, [filter]);

  return (
    <Box sx={{}}>
      {/* Selection panels left and right */}
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", md: "row" },
          gap: 2,
          alignItems: "stretch",
          mb: 2.5,
        }}
      >
        <SelectionPanel
          side="left"
          selection={leftSel}
          onChange={setLeftSel}
          plant={plant}
        />

        <Box
          display="flex"
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
          gap={1}
          sx={{ flexShrink: 0 }}
        >
          <CompareArrowsIcon sx={{ color: "text.disabled", fontSize: 28 }} />
          {matchRate !== null && (
            <Tooltip title="Overall match rate">
              <Chip
                label={`${matchRate}%`}
                size="small"
                color={
                  matchRate === 100
                    ? "success"
                    : matchRate >= 70
                      ? "warning"
                      : "error"
                }
                sx={{ fontWeight: 700 }}
              />
            </Tooltip>
          )}
        </Box>

        <SelectionPanel
          side="right"
          selection={rightSel}
          onChange={setRightSel}
          plant={plant}
        />
      </Box>

      {/* Validate button */}
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        gap={1.5}
        mb={3}
      >
        <Button
          variant="contained"
          size="large"
          onClick={handleValidate}
          disabled={running}
          startIcon={
            running ? (
              <CircularProgress size={16} color="inherit" />
            ) : (
              <CheckCircleOutlineIcon />
            )
          }
          sx={{ fontWeight: 700, px: 5, borderRadius: 2 }}
        >
          {running
            ? "Validating…"
            : `Validate ( ${leftLabel} - ${leftSel.type} ) vs ( ${rightLabel} - ${rightSel.type} )`}
        </Button>

        {compRows && (
          <Button
            variant="outlined"
            size="large"
            onClick={handleReset}
            disabled={running}
            startIcon={<RestartAltIcon />}
            sx={{
              fontWeight: 700,
              px: 3,
              borderRadius: 2,
            }}
          >
            Reset All
          </Button>
        )}
      </Box>

      {/* Results section */}
      {compRows && (
        <>
          <Divider sx={{ mb: 2 }} />
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            flexWrap="wrap"
            gap={2}
            mb={1.5}
          >
            <SummaryBar
              rows={compRows}
              filter={filter}
              onFilter={setFilter}
              leftSource={leftSel}
              rightSource={rightSel}
            />
            <FunctionsAfterValidation
              plant={plant}
              rows={compRows}
              setCompRows={setCompRows}
              filter={filter}
              selectedRows={selectedRows}
            />
          </Box>
          <ComparisonTable
            rows={compRows}
            setCompRows={setCompRows}
            filter={filter}
            leftSideLabel={`${leftLabel} - ${leftSel.type}`}
            rightSideLabel={`${rightLabel} - ${rightSel.type}`}
            selectedRows={selectedRows}
            setSelectedRows={setSelectedRows}
          />
        </>
      )}
    </Box>
  );
}
